import { Button } from '@shared/ui/Button';
import { Loader } from '@shared/ui/Progress';
import { Table } from '@shared/ui/Table';
import { DOC_TYPES, TEMPLATE_KIND_BY_DOC_TYPE, statusBadge } from './constants';
import {
  Bar,
  Empty,
  FilterLabel,
  FilterRow,
  KbDateCell,
  KbErrorMessage,
  KbFilterSelect,
  KbLoadingCell,
  KbMonoCell,
  KbRowActions,
  KbStatusWrapper,
  KbTableCard,
  KbTitleCell,
} from './styles';
import type { KnowledgeBaseTableProps } from './types';

export function KnowledgeBaseTable({
  items,
  isLoading,
  canWrite,
  filterType,
  source,
  onFilterChange,
  onUploadClick,
  onDetailsClick,
  onDownloadClick,
  onArchiveClick,
}: KnowledgeBaseTableProps) {
  return (
    <>
      <Bar>
        <FilterRow>
          <FilterLabel>Тип:</FilterLabel>
          <KbFilterSelect
            value={filterType}
            onChange={(e) => onFilterChange(e.target.value)}
          >
            <option value="">Все</option>
            {DOC_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </KbFilterSelect>
        </FilterRow>
        <Button
          onClick={onUploadClick}
          disabled={!canWrite}
          title={!canWrite ? 'Только чтение' : undefined}
        >
          Загрузить документы
        </Button>
      </Bar>

      <KbTableCard>
        <Table>
          <thead>
            <tr>
              <th>Название</th>
              <th>Тип</th>
              <th>Номер</th>
              <th>Версия</th>
              <th>Стр.</th>
              <th>Статус</th>
              <th>Загружен</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <KbLoadingCell colSpan={8}>
                  <Loader />
                </KbLoadingCell>
              </tr>
            )}
            {items.map((d) => (
              <tr key={d.id}>
                <KbTitleCell>{d.title}</KbTitleCell>
                <td>
                  {DOC_TYPES.find((t) => t.value === d.document_type)?.label ?? d.document_type}
                </td>
                <KbMonoCell className="mono">
                  {d.document_number ?? '—'}
                </KbMonoCell>
                <td>{d.version ?? '—'}</td>
                <td>{d.total_pages ?? '—'}</td>
                <td>
                  <KbStatusWrapper>
                    {statusBadge(d.status)}
                    {d.status === 'failed' && d.error_message && (
                      <KbErrorMessage title={d.error_message}>
                        {d.error_message}
                      </KbErrorMessage>
                    )}
                  </KbStatusWrapper>
                </td>
                <KbDateCell>
                  {new Date(d.uploaded_at).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </KbDateCell>
                <td>
                  <KbRowActions>
                    <Button
                      $variant="secondary"
                      $size="sm"
                      disabled={d.status !== 'active'}
                      title={
                        d.status !== 'active'
                          ? 'Доступно после индексации'
                          : source === 'rules'
                            ? 'Показать извлечённые правила'
                            : 'Показать извлечённые требования'
                      }
                      onClick={() => onDetailsClick(d)}
                    >
                      Подробнее
                    </Button>
                    {d.document_type in TEMPLATE_KIND_BY_DOC_TYPE && (
                      <Button
                        $variant="secondary"
                        $size="sm"
                        title="Скачать исходный DOCX шаблона"
                        onClick={() => onDownloadClick(d.id, d.title)}
                      >
                        Скачать
                      </Button>
                    )}
                    <Button
                      $variant="ghost"
                      $size="sm"
                      onClick={() => onArchiveClick(d.id)}
                      disabled={!canWrite}
                      title={!canWrite ? 'Только чтение' : undefined}
                    >
                      Архив
                    </Button>
                  </KbRowActions>
                </td>
              </tr>
            ))}
            {!isLoading && items.length === 0 && (
              <tr>
                <Empty colSpan={8}>
                  {source === ''
                    ? 'Документов нет. Загрузите первые ГОСТы и НПА.'
                    : source === 'rules'
                      ? 'Регламентов проверки пока нет. Загрузите чек-лист — агент извлечёт из него правила.'
                      : 'В этой группе документов пока нет. Загрузите подходящие или переключитесь на другой источник.'}
                </Empty>
              </tr>
            )}
          </tbody>
        </Table>
      </KbTableCard>
    </>
  );
}
